/**
 * Queue List View - Display queue tickets in table format
 */
import { QueueStatusBadge } from './QueueStatusBadge';
import { Button } from '@/components/ui/Button';
import { formatWaitTime } from '@/lib/timeUtils';
import type { QueueTicket, Barber, Service } from '@/types';

interface QueueListViewProps {
  tickets: QueueTicket[];
  barbers?: Barber[];
  services?: Service[];
  onCall?: (queueId: string) => void;
  onMarkArrival?: (queueId: string) => void;
  onComplete?: (queueId: string) => void;
  onCancel?: (queueId: string) => void;
  showActions?: boolean;
}

export function QueueListView({
  tickets,
  barbers,
  services,
  onCall,
  onMarkArrival,
  onComplete,
  onCancel,
  showActions = true,
}: QueueListViewProps) {
  const formatTime = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  };

  const getBarberName = (barberId: string | null | undefined) => {
    if (!barberId) return '-';
    const barber = barbers?.find(b => b.barberId === barberId);
    return barber?.displayName || barberId.slice(0, 8);
  };

  const getServiceName = (serviceId: string | null | undefined) => {
    if (!serviceId) return '-';
    const service = services?.find(s => s.serviceId === serviceId);
    return service?.name || serviceId.slice(0, 8);
  };

  const getEstimatedTime = (ticket: QueueTicket) => {
    if (!ticket.estimatedWaitTime) return '-';
    const now = new Date();
    const estimatedTime = new Date(now.getTime() + ticket.estimatedWaitTime * 60000);
    return estimatedTime.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  };

  const getActions = (ticket: QueueTicket) => {
    const canCall = ticket.status === 'waiting';
    const canMarkArrival = ticket.status === 'notified';
    const canComplete = ticket.status === 'arrived' || ticket.status === 'in_service';
    const canCancel = ['waiting', 'notified', 'arrived'].includes(ticket.status);

    return { canCall, canMarkArrival, canComplete, canCancel };
  };

  return (
    <div className="bg-surface shadow-md rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-hairline">
          <thead className="bg-surface-2">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider">
                Ticket #
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider">
                Posición
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider">
                Estado
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider">
                Cliente
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider">
                Barbero
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider">
                Servicio
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider">
                Espera
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider">
                Hora Est.
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider">
                Creado
              </th>
              {showActions && (
                <th className="px-6 py-3 text-right text-xs font-medium text-ink-subtle uppercase tracking-wider">
                  Acciones
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-surface divide-y divide-hairline">
            {tickets.map((ticket) => {
              const actions = getActions(ticket);

              return (
                <tr key={ticket.queueId} className="hover:bg-surface-2 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-mono font-bold text-ink">
                      #{ticket.ticketNumber}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-2xl font-bold text-info">
                      {(ticket.status === 'in_service' || ticket.status === 'finishing') ? '✂️' : ticket.position}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <QueueStatusBadge status={ticket.status} />
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-ink">
                      {ticket.clientName || 'Cliente'}
                    </div>
                    <div className="text-xs text-ink-subtle">
                      {ticket.clientEmail}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-ink">
                      {getBarberName(ticket.barberId)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-ink">
                      {getServiceName(ticket.serviceId)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-accent">
                      {formatWaitTime(ticket.estimatedWaitTime || 0)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-semibold text-info">
                      {getEstimatedTime(ticket)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-ink">
                      {formatTime(ticket.createdAt)}
                    </div>
                  </td>
                  {showActions && (
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        {actions.canCall && onCall && (
                          <Button
                            size="sm"
                            variant="primary"
                            onClick={() => onCall(ticket.queueId)}
                            title="Call ticket"
                          >
                            🔔
                          </Button>
                        )}
                        {actions.canMarkArrival && onMarkArrival && (
                          <Button
                            size="sm"
                            variant="success"
                            onClick={() => onMarkArrival(ticket.queueId)}
                            title="Mark arrival"
                          >
                            ✅
                          </Button>
                        )}
                        {actions.canComplete && onComplete && (
                          <Button
                            size="sm"
                            variant="success"
                            onClick={() => onComplete(ticket.queueId)}
                            title="Complete service"
                          >
                            🎉
                          </Button>
                        )}
                        {actions.canCancel && onCancel && (
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => onCancel(ticket.queueId)}
                            title="Cancel ticket"
                          >
                            ❌
                          </Button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
